import { Controller } from "@hotwired/stimulus"

// Wraps a table toolbar form. Debounces typing in the search input and
// submits the form, producing canonical Ransack URL params
// (e.g. ?q[full_name_or_email_cont]=alice).
//
// On connect, mirrors any existing `q[*]` URL params that are not yet
// represented in the form as hidden inputs. This preserves filter state
// across manual submits — e.g. when a column-level filter elsewhere has
// added params to the URL, those are kept when the main form re-submits.
//
// Generalized to use Ransack's `q[*]` namespace instead of a legacy
// `col_filter[*]` convention from a sibling project.
export default class extends Controller {
  static targets = ["query"]
  static values  = { debounce: { type: Number, default: 300 } }

  initialize() {
    this.debounceTimer = null
  }

  connect() {
    this.syncUrlParamsAsHiddenInputs()
  }

  // Called via data-action="input->filters#schedule" on the query input.
  schedule() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.submit(), this.debounceValue)
  }

  submit() {
    this.syncUrlParamsAsHiddenInputs()
    this.element.requestSubmit()
  }

  // For cases where requestSubmit() is invoked from elsewhere; wire via
  // data-action="submit->filters#syncOnSubmit" on the <form> if needed.
  syncOnSubmit() {
    this.syncUrlParamsAsHiddenInputs()
  }

  syncUrlParamsAsHiddenInputs() {
    // Drop previously-mirrored hidden inputs so we don't accumulate stale state.
    this.element
      .querySelectorAll('input[type="hidden"][data-filters-mirrored="true"]')
      .forEach((el) => el.remove())

    const formNames = new Set(
      Array.from(this.element.elements).map((el) => el.name).filter(Boolean)
    )

    const params = new URLSearchParams(window.location.search)
    for (const [name, value] of params) {
      if (!name.startsWith("q[")) continue
      if (formNames.has(name)) continue
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = name
      input.value = value
      input.dataset.filtersMirrored = "true"
      this.element.appendChild(input)
    }
  }
}
